import { useCallback, useState } from 'react'
import { useRecoilValue, useSetRecoilState } from 'recoil'
import { useTranslation } from 'react-i18next'
import { themeOverrideAtom } from '@/atoms'
import {
  serviceInformationTemplatesAtom,
  showServiceTemplatesSwapMenuAtom,
} from '@/atoms/_editorAtoms'
import {
  createServiceInformationTemplate,
  getServiceInformationTemplates,
} from '@/api/serviceInformationTemplates'
import { useSetToast } from '@/hooks'
import { useErrorHandler } from '@/hooks/utility/useErrorHandler'
import type { ServiceTemplateCategory } from '@messenger/types'

type UseServiceTemplatesProps = {
  editorRef: React.RefObject<Editor | null>
  /** Optional callback when template is successfully created */
  onCreateSuccess?: () => void
}

type UseServiceTemplatesReturn = {
  // State
  templateName: string
  setTemplateName: (name: string) => void
  templateCategory: ServiceTemplateCategory
  setTemplateCategory: (category: ServiceTemplateCategory) => void
  showModal: boolean
  setShowModal: (show: boolean) => void
  createdTemplateContent: string | null
  isCreating: boolean

  // Actions
  handleCreateTemplate: () => Promise<void>
  handleSwapTemplate: () => void
  isServiceInformationTemplate: (html?: string | null) => boolean
}

/**
 * Checks if content is empty (handles TinyMCE default empty paragraph)
 */
export const isContentEmpty = (content: string | undefined | null): boolean => {
  if (!content) return true
  const trimmed = content.trim()
  return !trimmed || trimmed === '<p>&nbsp;</p>'
}

/**
 * Checks if HTML string contains a service information template marker
 */
export const checkIsServiceInformationTemplate = (
  html: string | null,
): boolean => {
  if (!html) return false
  return html.includes('id="service-information-template"')
}

/**
 * Hook for managing service information templates in the editor
 *
 * @param editorRef - Reference to the TinyMCE editor instance
 * @param onCreateSuccess - Optional callback when template is successfully created
 * @returns Template management functions and state
 */
export const useServiceTemplates = ({
  editorRef,
  onCreateSuccess,
}: UseServiceTemplatesProps): UseServiceTemplatesReturn => {
  const { t } = useTranslation()
  const [templateName, setTemplateName] = useState('')
  const [templateCategory, setTemplateCategory] =
    useState<ServiceTemplateCategory>('DEFAULT')
  const [showModal, setShowModal] = useState(false)
  const [isCreating, setIsCreating] = useState(false)
  const [createdTemplateContent, setCreatedTemplateContent] = useState<
    string | null
  >(null)

  const themeOverride = useRecoilValue(themeOverrideAtom) as {
    product_id: string
  }
  const setServiceInformationTemplates = useSetRecoilState(
    serviceInformationTemplatesAtom,
  )
  const setShowServiceTemplatesSwapMenu = useSetRecoilState(
    showServiceTemplatesSwapMenuAtom,
  )

  const setToast = useSetToast()
  const handleError = useErrorHandler('useServiceTemplates')

  // Check if content contains a service information template
  const isServiceInformationTemplate = useCallback(
    (html: string | null = null): boolean => {
      const content = html ?? editorRef?.current?.getContent?.()
      return checkIsServiceInformationTemplate(content ?? null)
    },
    [editorRef],
  )

  // Create a new service information template from current editor content
  const handleCreateTemplate = useCallback(async () => {
    const content = editorRef?.current?.getContent?.()

    // Validate template name
    if (!templateName.trim()) {
      setToast({
        text: t('editor.serviceTemplates.notifications.enterName'),
        icon: 'error',
      })
      return
    }

    // Validate content
    if (isContentEmpty(content)) {
      setToast({
        text: t('editor.serviceTemplates.notifications.enterContent'),
        icon: 'error',
      })
      return
    }

    setIsCreating(true)

    try {
      const productId = themeOverride?.product_id

      if (!productId) {
        throw new Error(
          t('editor.serviceTemplates.notifications.productIdNotFound'),
        )
      }

      const updatedContent = `<div id="service-information-template">${content}</div>`

      await createServiceInformationTemplate({
        name: templateName,
        productId: productId,
        template: updatedContent,
        category: templateCategory,
      })

      const serviceInformationTemplatesData =
        await getServiceInformationTemplates(productId)

      setServiceInformationTemplates(serviceInformationTemplatesData.data)

      setToast({
        text: t('editor.serviceTemplates.notifications.templateCreated'),
        icon: 'success',
      })

      setCreatedTemplateContent(updatedContent)
      setShowModal(false)
      setTemplateName('')
      setTemplateCategory('DEFAULT')

      // Call optional success callback
      onCreateSuccess?.()
    } catch (error) {
      const err = error as {
        response?: { data?: { message?: string } }
        message?: string
      }
      handleError(
        err?.response?.data?.message ||
          err?.message ||
          t('editor.serviceTemplates.notifications.failedToCreateTemplate'),
        error as Error,
      )
    } finally {
      setIsCreating(false)
    }
  }, [
    editorRef,
    templateName,
    templateCategory,
    themeOverride,
    setToast,
    handleError,
    setServiceInformationTemplates,
    onCreateSuccess,
    t,
  ])

  // Open the swap template menu
  const handleSwapTemplate = useCallback(() => {
    setShowServiceTemplatesSwapMenu(true)
  }, [setShowServiceTemplatesSwapMenu])

  return {
    templateName,
    setTemplateName,
    templateCategory,
    setTemplateCategory,
    showModal,
    setShowModal,
    createdTemplateContent,
    isCreating,
    handleCreateTemplate,
    handleSwapTemplate,
    isServiceInformationTemplate,
  }
}
